/**
 * Gift wrap / additional service checkbox logic for cart drawer.
 *
 * Reads configuration lazily from data-* attributes on
 * .wt-cart__gift-wrap-block so it always picks up fresh values
 * after Section Rendering API replaces the cart drawer HTML.
 *
 * All listeners use event delegation on document.body and are
 * registered once — they survive any number of innerHTML refreshes.
 */
(() => {
  const DEBUG = false;
  const MAX_ITEMS_PER_BOX = 3;

  let requestInFlight = false;
  let _reconciling = false;
  let _lastReconcileAt = 0;
  let _reconcileTimer = null;
  let _reconcileSubBound = false;

  function getConfig() {
    const el = document.querySelector('.wt-cart__gift-wrap-block[data-variant-id]');
    if (!el) return null;
    const raw = el.dataset.variantId;
    const variantId = raw ? parseInt(raw, 10) : null;
    return {
      variantId: variantId && !isNaN(variantId) ? variantId : null,
      hasProduct: el.dataset.hasProduct === 'true',
      checkboxLabel: el.dataset.checkboxLabel || 'Gift wrapping',
      useGiftWrapModal: el.dataset.useGiftWrapModal !== 'false',
      contentsLabel: el.dataset.contentsLabel || '',
      notSpecifiedLabel: el.dataset.notSpecifiedLabel || '',
    };
  }

  function refreshCartSections() {
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-additional-service' });
    }
    const cartFooter = document.getElementById('main-cart-footer');
    if (
      cartFooter &&
      cartFooter.dataset.id &&
      (window.location.pathname === (window.routes?.cart_url || '/cart') ||
        window.location.pathname.endsWith('/cart'))
    ) {
      const sectionId = encodeURIComponent(cartFooter.dataset.id);
      fetch((window.routes?.cart_url || '/cart') + '?section_id=' + sectionId)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newFooter = doc.getElementById('main-cart-footer');
          if (newFooter) cartFooter.innerHTML = newFooter.innerHTML;
        })
        .catch(() => {});
    }
  }

  function getGiftWrapLines(cart, variantId) {
    const lines = [];
    (cart.items || []).forEach((item, i) => {
      if (
        item.variant_id === variantId &&
        item.properties &&
        item.properties._gift_wrap === 'true'
      ) {
        lines.push({
          line: i + 1,
          item,
          variantIds: (item.properties._gift_wrap_variant_ids || '')
            .split(',')
            .filter(Boolean)
            .map((id) => parseInt(id, 10)),
        });
      }
    });
    return lines;
  }

  function getCartVariantCounts(cart, variantId) {
    const counts = {};
    (cart.items || []).forEach((item) => {
      if (
        item.variant_id === variantId &&
        item.properties &&
        item.properties._gift_wrap === 'true'
      )
        return;
      counts[item.variant_id] = (counts[item.variant_id] || 0) + (item.quantity || 1);
    });
    return counts;
  }

  function getCartVariantTitles(cart, variantId) {
    const titles = {};
    (cart.items || []).forEach((item) => {
      if (
        item.variant_id === variantId &&
        item.properties &&
        item.properties._gift_wrap === 'true'
      )
        return;
      if (titles[item.variant_id] == null && (item.product_title || item.title)) {
        titles[item.variant_id] = (item.product_title || item.title).trim();
      }
    });
    return titles;
  }

  async function reconcileGiftWrapContents() {
    const cfg = getConfig();
    if (!cfg || !cfg.hasProduct || !cfg.variantId) return;
    if (_reconciling) return;
    if (Date.now() - _lastReconcileAt < 2000) return;
    _reconciling = true;
    try {
      const cartJsonUrl = (window.routes?.cart_url || '/cart') + '.js';
      const cartRes = await fetch(cartJsonUrl);
      const cart = await cartRes.json();
      const giftWrapLines = getGiftWrapLines(cart, cfg.variantId);
      if (giftWrapLines.length === 0) return;
      const variantCounts = getCartVariantCounts(cart, cfg.variantId);
      const variantTitles = getCartVariantTitles(cart, cfg.variantId);
      const changeUrl = (window.routes?.cart_change_url || '/cart/change').replace(
        /\/change$/,
        '/change.js'
      );
      const addUrl = (window.routes?.cart_add_url || '/cart/add').replace(/\/add$/, '/add.js');
      const toUpdate = [];
      for (const { item, variantIds } of giftWrapLines) {
        if (variantIds.length === 0) continue;
        const counts = Object.assign({}, variantCounts);
        const newIds = [];
        variantIds.forEach(function (id) {
          if (counts[id] > 0) {
            newIds.push(id);
            counts[id]--;
          }
        });
        const newVariantIdsStr = newIds.join(',');
        const currentIdsStr = (item.properties._gift_wrap_variant_ids || '').trim();
        if (newVariantIdsStr === currentIdsStr) continue;
        const titleCounts = {};
        newIds.forEach(function (id) {
          var t = variantTitles[id] || 'Variant ' + id;
          titleCounts[t] = (titleCounts[t] || 0) + 1;
        });
        var newTitles = Object.entries(titleCounts)
          .map(function (e) {
            return e[1] + 'x ' + e[0];
          })
          .join(', ');
        var props = {
          _gift_wrap: 'true',
          _gift_wrap_box: (item.properties._gift_wrap_box || '').trim() || '1',
          _gift_wrap_variant_ids: newVariantIdsStr,
          _gift_wrap_titles: newTitles,
        };
        props[cfg.contentsLabel] = newTitles || cfg.notSpecifiedLabel;
        toUpdate.push({ key: item.key, properties: props });
      }
      if (toUpdate.length === 0) return;
      if (DEBUG) console.log('[cart-checkbox] reconciling', toUpdate.length, 'gift wrap lines');
      for (var u = 0; u < toUpdate.length; u++) {
        await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: toUpdate[u].key, quantity: 0 }),
        });
      }
      var addItems = toUpdate.map(function (u) {
        return { id: cfg.variantId, quantity: 1, properties: u.properties };
      });
      await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: addItems }),
      });
      if (DEBUG) console.log('[cart-checkbox] reconciled', toUpdate.length, 'lines via remove+re-add');
      refreshCartSections();
    } catch (err) {
      if (DEBUG) console.warn('[cart-checkbox] reconcile error', err);
    } finally {
      _reconciling = false;
      _lastReconcileAt = Date.now();
    }
  }

  function scheduleReconcile(source) {
    const cfg = getConfig();
    if (!cfg || !cfg.hasProduct || !cfg.variantId) return;
    if (source === 'cart-additional-service') return;
    if (_reconciling) return;
    if (_reconcileTimer) clearTimeout(_reconcileTimer);
    _reconcileTimer = setTimeout(function () {
      _reconcileTimer = null;
      reconcileGiftWrapContents();
    }, 350);
  }

  function ensureReconcileSubs() {
    if (_reconcileSubBound) return;
    _reconcileSubBound = true;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (ev) {
        scheduleReconcile(ev && ev.source);
      });
    }
    document.addEventListener('cart-drawer:refresh', function () {
      scheduleReconcile('cart-drawer');
    });
    scheduleReconcile('init');
  }

  function isFreeItem(item) {
    const price =
      item.final_line_price != null
        ? item.final_line_price
        : item.price != null
          ? item.price * (item.quantity || 1)
          : 0;
    return price === 0;
  }

  function isBundleItem(item) {
    if (item.tags) {
      const tags = Array.isArray(item.tags)
        ? item.tags
        : item.tags.split(',').map((t) => t.trim());
      if (
        tags.some((tag) => {
          const t = tag.toLowerCase();
          return (
            t.includes('balíček') ||
            t.includes('balicek') ||
            t.includes('balík') ||
            t.includes('balik')
          );
        })
      ) {
        return true;
      }
    }
    const type = (item.product_type || '').toLowerCase();
    const title = (item.product_title || item.title || '').toLowerCase();
    const handle = (item.handle || '').toLowerCase();
    return (
      type.includes('bundle') ||
      type.includes('balíček') ||
      type.includes('balík') ||
      type.includes('set') ||
      title.includes('balíček') ||
      title.includes('balík') ||
      title.includes('bundle') ||
      title.includes(' set:') ||
      title.includes(' set ') ||
      handle.includes('bundle') ||
      handle.includes('balicek') ||
      handle.includes('-set-') ||
      handle.includes('set-')
    );
  }

  function getSelectableItems(cart, variantId, wrappedCounts) {
    return (cart.items || [])
      .filter((item) => item.variant_id !== variantId && !isFreeItem(item))
      .map((item) => {
        const wrapped = (wrappedCounts && wrappedCounts[item.variant_id]) || 0;
        return Object.assign({}, item, { _remainingQty: item.quantity - wrapped });
      })
      .filter((item) => item._remainingQty > 0);
  }

  function openGiftWrapModal(openerCheckbox, addAnother) {
    const cfg = getConfig();
    if (!cfg) return;

    const allModals = document.querySelectorAll('#GiftWrapModal');
    if (DEBUG && allModals.length > 1) {
      console.warn('[cart-checkbox] duplicate modals found:', allModals.length, '- removing extras');
      for (let i = 1; i < allModals.length; i++) allModals[i].remove();
    }
    const modalEl = document.getElementById('GiftWrapModal');
    const modalItemsEl = document.getElementById('gift-wrap-modal-items');
    const modalConfirmBtn = document.getElementById('GiftWrapModalConfirm');
    if (DEBUG)
      console.log('[cart-checkbox] openModal', {
        hasModal: !!modalEl,
        hasItemsEl: !!modalItemsEl,
      });
    if (!modalEl || !modalItemsEl) return;

    const opener = openerCheckbox || null;
    if (modalEl.show && typeof modalEl.show === 'function') {
      modalEl.show(opener);
    } else {
      modalEl.setAttribute('open', '');
    }
    modalEl.setAttribute('open', '');
    modalEl.style.setProperty('display', 'flex', 'important');

    modalEl._giftWrapOpenerCheckbox = openerCheckbox || null;
    modalEl._giftWrapAddAnother = !!addAnother;
    if (modalConfirmBtn) modalConfirmBtn.disabled = true;
    modalItemsEl.innerHTML = '';

    const cartJsonUrl = (window.routes?.cart_url || '/cart') + '.js';
    fetch(cartJsonUrl)
      .then((r) => r.json())
      .then((cart) => {
        const giftWrapLines = getGiftWrapLines(cart, cfg.variantId);
        const wrappedCounts = {};
        if (addAnother) {
          giftWrapLines.forEach((l) =>
            l.variantIds.forEach((id) => {
              wrappedCounts[id] = (wrappedCounts[id] || 0) + 1;
            })
          );
        }
        const selectable = getSelectableItems(
          cart,
          cfg.variantId,
          addAnother ? wrappedCounts : null
        );
        const nextBox = giftWrapLines.length + 1;
        modalEl._giftWrapBoxNumber = nextBox;

        function updateRowStates() {
          const checked = modalItemsEl.querySelectorAll('.gift-wrap-checkbox__input:checked');
          const checkedCount = checked.length;
          const hasBundle = Array.from(checked).some((c) => c.dataset.isBundle === '1');
          const allRows = modalItemsEl.querySelectorAll('.gift-wrap-modal__row');
          allRows.forEach((row) => {
            const input = row.querySelector('.gift-wrap-checkbox__input');
            const isBundle = input.dataset.isBundle === '1';
            const isChecked = input.checked;
            let shouldDisable = false;
            if (!isChecked) {
              if (hasBundle) {
                shouldDisable = true;
              } else if (isBundle && checkedCount > 0) {
                shouldDisable = true;
              } else if (!isBundle && checkedCount >= MAX_ITEMS_PER_BOX) {
                shouldDisable = true;
              }
            }
            input.disabled = shouldDisable;
            if (shouldDisable) {
              row.classList.add('tw-opacity-50', 'tw-pointer-events-none');
            } else {
              row.classList.remove('tw-opacity-50', 'tw-pointer-events-none');
            }
          });
          const valid = checkedCount > 0 && checkedCount <= (hasBundle ? 1 : MAX_ITEMS_PER_BOX);
          const btn = document.getElementById('GiftWrapModalConfirm');
          if (btn) btn.disabled = !valid;
        }

        selectable.forEach((item) => {
          const bundle = isBundleItem(item);
          const remaining = item._remainingQty != null ? item._remainingQty : item.quantity || 1;
          for (let unit = 1; unit <= remaining; unit++) {
            const row = document.createElement('label');
            row.className = 'gift-wrap-modal__row';
            row.setAttribute('role', 'listitem');

            const checkboxWrapper = document.createElement('span');
            checkboxWrapper.className = 'gift-wrap-checkbox';

            const input = document.createElement('input');
            input.type = 'checkbox';
            input.className = 'gift-wrap-checkbox__input';
            input.dataset.variantId = String(item.variant_id);
            input.dataset.itemTitle = (item.product_title || item.title || '').replace(/"/g, '');
            input.dataset.isBundle = bundle ? '1' : '0';
            input.dataset.unit = String(unit);
            input.addEventListener('change', updateRowStates);

            const checkboxVisual = document.createElement('span');
            checkboxVisual.className = 'gift-wrap-checkbox__visual';
            checkboxVisual.innerHTML =
              '<svg class="gift-wrap-checkbox__check" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72 72"><path d="m25.73166,49.0691l-14.3458,-14.3458l-4.88592,4.84922l19.23631,19.23172l41.28947,-41.28947l-4.85381,-4.84922l-36.44025,36.40355z" class="icon-path-check"></path></svg>';

            checkboxWrapper.appendChild(input);
            checkboxWrapper.appendChild(checkboxVisual);

            const imgWrap = document.createElement('span');
            imgWrap.className = 'gift-wrap-modal__thumb';
            const imgUrl =
              (item.image && item.image.url) || (item.featured_image && item.featured_image.url);
            if (imgUrl) {
              const img = document.createElement('img');
              img.src = imgUrl;
              img.alt =
                (item.image && item.image.alt) ||
                (item.featured_image && item.featured_image.alt) ||
                item.product_title ||
                item.title ||
                '';
              img.loading = 'lazy';
              imgWrap.appendChild(img);
            }

            const textWrap = document.createElement('span');
            textWrap.className = 'gift-wrap-modal__text';
            const titleEl = document.createElement('span');
            titleEl.className = 'gift-wrap-modal__title';
            titleEl.textContent = item.product_title || item.title;
            textWrap.appendChild(titleEl);
            if (bundle) {
              const badge = document.createElement('span');
              badge.className = 'gift-wrap-modal__badge';
              badge.textContent = 'Balík';
              textWrap.appendChild(badge);
            }
            row.appendChild(checkboxWrapper);
            row.appendChild(imgWrap);
            row.appendChild(textWrap);
            modalItemsEl.appendChild(row);
          }
        });
        updateRowStates();
        if (DEBUG)
          console.log('[cart-checkbox] modal opened', {
            addAnother,
            nextBox,
            selectableCount: selectable.length,
          });
      })
      .catch((err) => {
        if (DEBUG) console.error('[cart-checkbox] modal cart fetch failed', err);
        modalItemsEl.innerHTML =
          '<p class="tw-text-sm tw-text-body-light">Unable to load cart.</p>';
      });
  }

  function closeGiftWrapModal() {
    const modalEl = document.getElementById('GiftWrapModal');
    if (!modalEl) return;
    if (modalEl.hide && typeof modalEl.hide === 'function') {
      modalEl.hide();
    } else {
      modalEl.removeAttribute('open');
    }
    modalEl.style.removeProperty('display');
    modalEl._giftWrapOpenerCheckbox = null;
    modalEl._giftWrapAddAnother = false;
  }

  function onGiftWrapModalConfirm() {
    const cfg = getConfig();
    if (!cfg) return;

    const modalEl = document.getElementById('GiftWrapModal');
    const modalItemsEl = document.getElementById('gift-wrap-modal-items');
    const modalConfirmBtn = document.getElementById('GiftWrapModalConfirm');
    if (!modalEl || !modalItemsEl) return;

    const checked = modalItemsEl.querySelectorAll('.gift-wrap-checkbox__input:checked');
    if (checked.length === 0) return;
    const hasBundle = Array.from(checked).some((c) => c.dataset.isBundle === '1');
    if (hasBundle && checked.length > 1) return;
    if (!hasBundle && checked.length > MAX_ITEMS_PER_BOX) return;

    const variantIds = Array.from(checked)
      .map((c) => c.dataset.variantId)
      .join(',');
    const titleCounts = {};
    Array.from(checked).forEach((c) => {
      const title = c.dataset.itemTitle || '';
      if (title) {
        titleCounts[title] = (titleCounts[title] || 0) + 1;
      }
    });
    const titles = Object.entries(titleCounts)
      .map(([title, count]) => `${count}x ${title}`)
      .join(', ');
    const boxNumber = modalEl._giftWrapBoxNumber || 1;
    const properties = {
      _gift_wrap: 'true',
      _gift_wrap_box: String(boxNumber),
      _gift_wrap_variant_ids: variantIds,
      _gift_wrap_titles: titles,
    };
    properties[cfg.contentsLabel] = titles;

    const body = JSON.stringify({
      items: [{ id: cfg.variantId, quantity: 1, properties: properties }],
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });
    const addUrl = (window.routes?.cart_add_url || '/cart/add').replace(/\/add$/, '/add.js');

    if (DEBUG)
      console.log('[cart-checkbox] modal add', { boxNumber, variantIds, body: JSON.parse(body) });
    modalConfirmBtn.disabled = true;

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.status >= 400) {
          if (DEBUG) console.error('[cart-checkbox] modal add error', data);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, { message: data.description || data.message });
          }
          modalConfirmBtn.disabled = false;
          return;
        }
        closeGiftWrapModal();
        refreshCartSections();
      })
      .catch((err) => {
        if (DEBUG) console.error('[cart-checkbox] modal add failed', err);
        modalConfirmBtn.disabled = false;
      });
  }

  function onGiftWrapModalCancel() {
    const modalEl = document.getElementById('GiftWrapModal');
    const opener = modalEl ? modalEl._giftWrapOpenerCheckbox : null;
    closeGiftWrapModal();
    if (opener) opener.checked = false;
  }

  // --- Delegated event listeners (registered once, survive innerHTML refreshes) ---

  document.body.addEventListener('click', (e) => {
    if (e.target.id === 'gift-wrap-add-another') {
      e.preventDefault();
      openGiftWrapModal(null, true);
    } else if (e.target.id === 'GiftWrapModalConfirm') {
      onGiftWrapModalConfirm();
    } else if (
      e.target.id === 'GiftWrapModalCancel' ||
      e.target.id === 'ModalClose-GiftWrapModal'
    ) {
      onGiftWrapModalCancel();
    }
  });

  document.body.addEventListener('modalClosed', () => {
    const modalEl = document.getElementById('GiftWrapModal');
    if (modalEl) {
      modalEl.style.removeProperty('display');
      const opener = modalEl._giftWrapOpenerCheckbox;
      if (opener) {
        opener.checked = false;
        modalEl._giftWrapOpenerCheckbox = null;
      }
    }
  });

  document.body.addEventListener(
    'change',
    async (e) => {
      if (e.target.id !== 'cart_additional_checkbox') return;

      const cfg = getConfig();
      if (!cfg) return;

      if (requestInFlight) return;

      const checkbox = e.target;
      const label = checkbox.dataset.label || cfg.checkboxLabel;
      const isChecked = checkbox.checked;
      checkbox.disabled = true;
      requestInFlight = true;

      try {
        if (cfg.hasProduct && cfg.variantId) {
          if (isChecked) {
            const modalEl = document.getElementById('GiftWrapModal');
            const modalItemsEl = document.getElementById('gift-wrap-modal-items');
            if (cfg.useGiftWrapModal && modalEl && modalItemsEl) {
              requestInFlight = false;
              checkbox.disabled = false;
              openGiftWrapModal(checkbox, false);
              return;
            }
            if (DEBUG) console.log('[cart-checkbox] modal not available, adding product directly');
            const properties = { _gift_wrap: 'true' };
            properties[cfg.contentsLabel] = cfg.notSpecifiedLabel;
            const body = JSON.stringify({
              items: [{ id: cfg.variantId, quantity: 1, properties: properties }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            });
            const addUrl = (window.routes?.cart_add_url || '/cart/add').replace(
              /\/add$/,
              '/add.js'
            );
            const res = await fetch(addUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body,
            });
            let data;
            try {
              data = await res.json();
            } catch (parseErr) {
              if (DEBUG) console.error('[cart-checkbox] add response not JSON');
              checkbox.checked = !isChecked;
              return;
            }
            if (!res.ok) {
              const msg = data?.description || data?.message || res.statusText;
              console.error('[cart-checkbox] add failed', { status: res.status, message: msg });
              checkbox.checked = !isChecked;
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, { message: msg });
              }
              return;
            }
            if (data.status && data.status >= 400) {
              console.error('[cart-checkbox] add error payload', data);
              checkbox.checked = !isChecked;
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, { message: data.description || data.message });
              }
              return;
            }
          } else {
            const cartJsonUrl = (window.routes?.cart_url || '/cart') + '.js';
            const cartRes = await fetch(cartJsonUrl);
            const cart = await cartRes.json();
            const giftWrapLines = getGiftWrapLines(cart, cfg.variantId);
            const changeUrl = (window.routes?.cart_change_url || '/cart/change').replace(
              /\/change$/,
              '/change.js'
            );
            for (const { line } of giftWrapLines) {
              const changeBody = JSON.stringify({
                line,
                quantity: 0,
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname,
              });
              await fetch(changeUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: changeBody,
              });
            }
            if (DEBUG)
              console.log('[cart-checkbox] removed gift wrap lines', giftWrapLines.length);
          }
          refreshCartSections();
        } else {
          if (DEBUG) console.log('[cart-checkbox] no product path (attributes)');
          const body = JSON.stringify({
            attributes: { [label]: isChecked },
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          });
          const updateUrl = (window.routes?.cart_update_url || '/cart/update').replace(
            /\/update$/,
            '/update.js'
          );
          await fetch(updateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body,
          });
          document.dispatchEvent(new CustomEvent('cart-drawer:refresh', { bubbles: true }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-additional-service' });
          }
          const cartFooter = document.getElementById('main-cart-footer');
          if (
            cartFooter &&
            cartFooter.dataset.id &&
            (window.location.pathname === (window.routes?.cart_url || '/cart') ||
              window.location.pathname.endsWith('/cart'))
          ) {
            const sectionId = encodeURIComponent(cartFooter.dataset.id);
            fetch((window.routes?.cart_url || '/cart') + '?section_id=' + sectionId)
              .then((r) => r.text())
              .then((html) => {
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const newFooter = doc.getElementById('main-cart-footer');
                if (newFooter) cartFooter.innerHTML = newFooter.innerHTML;
              })
              .catch(() => {});
          }
        }
      } catch (err) {
        console.error('[cart-checkbox] error', err);
        checkbox.checked = !isChecked;
      } finally {
        checkbox.disabled = false;
        requestInFlight = false;
      }

      ensureReconcileSubs();
    },
    { passive: false }
  );

  // Bind reconcile subscriptions once config is available
  ensureReconcileSubs();
})();
